/**
 * شهرِ دیفالت — قرینه‌ی apps/mobile/app/city.tsx.
 *
 * تا دیروز این دو تا فیلد ته صفحه‌ی نمایه بودن، کنارِ اسم و عکس. جاشون اونجا
 * نبود: شهر «اطلاعاتِ من» نیست، تنظیمِ خودِ اپه — از همین‌جاست که معلوم می‌شه
 * فهرستِ کشف چی نشون بده.
 *
 * دکمه‌ی «ذخیره» نداره. کشور رو که انتخاب کنی فهرستِ شهرها می‌آد، و انتخابِ
 * شهر خودش ذخیره‌ست: یه انتخابِ تک‌مرحله‌ای، دکمه‌ی تاییدِ جدا نمی‌خواد.
 */
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cityRepo } from '@/core/data/cityRepo';
import { profileRepo } from '@/core/data/socialRepo';
import { useSession } from '@/core/session';
import { setHomeCity } from '@/core/lib/cityScope';
import { t, useLocale } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { Card } from '@/ui/kit';
import { WhereYouLive, useWhereYouLive } from '@/features/profile/WhereYouLive';

export function CityPage() {
  useLocale();
  const { profile, refreshProfile } = useSession();
  const qc = useQueryClient();
  const nav = useNavigate();
  const location = useLocation();

  const place = useWhereYouLive();
  useEffect(() => {
    if (!profile) return;
    place.load(profile.country, profile.city, profile.cityId ?? null);
  }, [profile?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * اسم و شناسه با هم نوشته می‌شن — نگاه کن به Profile.cityId.
   *
   * `setHomeCity` هم همین‌جا صدا زده می‌شه نه بعد از `refreshProfile`: فهرستِ
   * کشف باید همون لحظه عوض شه، نه بعد از یه رفت‌وبرگشتِ دیگه به سرور.
   */
  const save = useMutation({
    mutationFn: async (picked: { city: string; cityId: number; country: string }) => {
      await profileRepo.updateMine(profile!.id, picked);
      const full = await cityRepo.byId(picked.cityId);
      if (full) setHomeCity(full);
    },
    onSuccess: async () => {
      await refreshProfile();
      qc.invalidateQueries();
      /**
       * ذخیره شد، پس این صفحه دیگه کاری نداره.
       *
       * موندن روی یه فرمِ تمام‌شده یعنی کاربر باید خودش بفهمه که کار تموم
       * شده و از دکمه‌ی back برگرده. برمی‌گردیم همون‌جایی که ازش اومده —
       * تنظیمات یا نمایه — و تاییدِ کار همون‌جاست: اسمِ شهرِ تازه روی ردیفِ
       * «شهر پیش‌فرض» نشسته.
       *
       * `location.key === 'default'` یعنی این اولین صفحه‌ی همین نشستِ
       * مرورگره (لینکِ مستقیم، یا رفرش). اون‌وقت `back` آدم رو از سایت
       * پرت می‌کنه بیرون، پس صریح می‌ریم به تنظیمات.
       */
      if (location.key === 'default') nav('/settings');
      else nav(-1);
    },
    onError: (e) => { reportError('web.settings.city', e); },
  });

  if (!profile) return <div className="feed"><p className="dim">…</p></div>;

  return (
    <div className="feed">
      <h1 className="feed-title">{t('defaultCity')}</h1>

      <Card>
        <p className="dim">{t('defaultCityHint')}</p>
        <WhereYouLive state={{
          ...place,
          // خودِ انتخاب یعنی ذخیره. شهرِ خالی (که با عوض شدنِ کشور پیش می‌آد)
          // چیزی برای ذخیره کردن نداره.
          setCity: (name, id) => {
            place.setCity(name, id);
            if (name && id) {
              save.mutate({ city: name, cityId: id, country: place.country?.name ?? '' });
            }
          },
        }} />
        {save.isPending ? <p className="dim">{t('saving')}</p> : null}
        {save.isError ? <p className="dim">{t('errorRetry')}</p> : null}
      </Card>
    </div>
  );
}
